import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** Pequena etiqueta acima do titulo (uppercase). */
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  /** Acoes no canto direito (botoes, filtros). */
  actions?: ReactNode;
  /** Rota de "voltar"; quando definida, exibe um botao a esquerda do titulo. */
  backTo?: string;
  /** Rotulo acessivel do botao voltar. */
  backLabel?: string;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  backTo,
  backLabel = "Voltar",
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "animate-fade-up relative flex flex-wrap items-end justify-between gap-4 pl-5",
        className,
      )}
    >
      <span className="absolute left-0 top-1 h-[calc(100%-0.5rem)] w-[3px] rounded-full bg-gradient-to-b from-brand-500 to-brand-700" />
      {backTo ? (
        <Link
          to={backTo}
          aria-label={backLabel}
          title={backLabel}
          className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-lg border border-line bg-surface text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {eyebrow ? (
          <span className="inline-flex h-5 w-fit items-center gap-1 rounded-md bg-brand-500 px-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white">
            {eyebrow}
          </span>
        ) : null}
        <h1 className="text-balance text-[28px] font-semibold leading-[1.05] tracking-[-0.02em] text-ink-950 sm:text-[32px]">
          {title}
        </h1>
        {description ? (
          <p className="max-w-2xl text-sm text-ink-500">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}
